import React, { useEffect, useMemo, useState } from "react";
import {
  CaretLeft,
  ArrowsLeftRight,
  Clock,
  CaretDown,
  CircleNotch,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatMoney } from "../../lib/currency";
import { fitClass } from "../../lib/fitClass";
import { addHistory } from "../../lib/historyLog";
import HistorySheet from "./HistorySheet";
import CurrencyTicker from "./CurrencyTicker";
import { MoneyTipButton, MoneyTipSheet } from "./MoneyTipPopover";
import { TICKER_ENABLED } from "../../lib/featureFlags";

const RATE_LS_KEY = (from, to) => `tiplingo_converter_rate_v1::${from}->${to}`;

// Size buckets tuned for the Currency page's text-5xl base displays.
// The container is ~230px wide (screen − padding − dropdown), so at
// text-5xl (~48px) we can only fit ~7 chars comfortably. These sizes
// are deliberately more aggressive than TipPanel's defaults because
// we start from a bigger baseline. Empirically tested against long
// currencies: KRW (~1.5M for $100), IDR (~1.5M for $100), VND
// (~2.5M for $100), plus user-entered amounts up to 10 digits.
const CURRENCY_FIT_SIZES = {
  lg14: "text-lg",     // 14+ chars: 14853200000.00
  lg12: "text-xl",     // 12-13 chars: 148532000.00
  lg10: "text-2xl",    // 10-11 chars: 1485320.00
  lg9:  "text-3xl",    // 8-9 chars:  148532.00 / 50000000
  dflt: "text-5xl",    // ≤7 chars:   16235.00
};
// Override lg9 threshold: kick in at len >= 8 so 8-digit inputs
// (e.g. "50000000") drop to text-3xl before hitting the right edge.
CURRENCY_FIT_SIZES.lg8 = "text-3xl"; // reserved for future extension

const FALLBACK_CURRENCIES = [
  { code: "USD", name: "US Dollar", flag: "🇺🇸" },
  { code: "EUR", name: "Euro", flag: "🇪🇺" },
  { code: "GBP", name: "British Pound", flag: "🇬🇧" },
  { code: "JPY", name: "Japanese Yen", flag: "🇯🇵" },
  { code: "CAD", name: "Canadian Dollar", flag: "🇨🇦" },
  { code: "AUD", name: "Australian Dollar", flag: "🇦🇺" },
  { code: "NZD", name: "New Zealand Dollar", flag: "🇳🇿" },
  { code: "CHF", name: "Swiss Franc", flag: "🇨🇭" },
  { code: "CNY", name: "Chinese Yuan", flag: "🇨🇳" },
  { code: "HKD", name: "Hong Kong Dollar", flag: "🇭🇰" },
  { code: "TWD", name: "Taiwan Dollar", flag: "🇹🇼" },
  { code: "SGD", name: "Singapore Dollar", flag: "🇸🇬" },
  { code: "KRW", name: "South Korean Won", flag: "🇰🇷" },
  { code: "INR", name: "Indian Rupee", flag: "🇮🇳" },
  { code: "THB", name: "Thai Baht", flag: "🇹🇭" },
  { code: "VND", name: "Vietnamese Dong", flag: "🇻🇳" },
  { code: "IDR", name: "Indonesian Rupiah", flag: "🇮🇩" },
  { code: "PHP", name: "Philippine Peso", flag: "🇵🇭" },
  { code: "MYR", name: "Malaysian Ringgit", flag: "🇲🇾" },
  { code: "PKR", name: "Pakistani Rupee", flag: "🇵🇰" },
  { code: "BDT", name: "Bangladeshi Taka", flag: "🇧🇩" },
  { code: "LKR", name: "Sri Lankan Rupee", flag: "🇱🇰" },
  { code: "MXN", name: "Mexican Peso", flag: "🇲🇽" },
  { code: "BRL", name: "Brazilian Real", flag: "🇧🇷" },
  { code: "ARS", name: "Argentine Peso", flag: "🇦🇷" },
  { code: "CLP", name: "Chilean Peso", flag: "🇨🇱" },
  { code: "COP", name: "Colombian Peso", flag: "🇨🇴" },
  { code: "PEN", name: "Peruvian Sol", flag: "🇵🇪" },
  { code: "UYU", name: "Uruguayan Peso", flag: "🇺🇾" },
  { code: "CRC", name: "Costa Rican Colón", flag: "🇨🇷" },
  { code: "SEK", name: "Swedish Krona", flag: "🇸🇪" },
  { code: "NOK", name: "Norwegian Krone", flag: "🇳🇴" },
  { code: "DKK", name: "Danish Krone", flag: "🇩🇰" },
  { code: "ISK", name: "Icelandic Króna", flag: "🇮🇸" },
  { code: "PLN", name: "Polish Złoty", flag: "🇵🇱" },
  { code: "CZK", name: "Czech Koruna", flag: "🇨🇿" },
  { code: "HUF", name: "Hungarian Forint", flag: "🇭🇺" },
  { code: "RON", name: "Romanian Leu", flag: "🇷🇴" },
  { code: "BGN", name: "Bulgarian Lev", flag: "🇧🇬" },
  { code: "TRY", name: "Turkish Lira", flag: "🇹🇷" },
  { code: "RUB", name: "Russian Ruble", flag: "🇷🇺" },
  { code: "UAH", name: "Ukrainian Hryvnia", flag: "🇺🇦" },
  { code: "AED", name: "UAE Dirham", flag: "🇦🇪" },
  { code: "SAR", name: "Saudi Riyal", flag: "🇸🇦" },
  { code: "QAR", name: "Qatari Riyal", flag: "🇶🇦" },
  { code: "KWD", name: "Kuwaiti Dinar", flag: "🇰🇼" },
  { code: "BHD", name: "Bahraini Dinar", flag: "🇧🇭" },
  { code: "OMR", name: "Omani Rial", flag: "🇴🇲" },
  { code: "JOD", name: "Jordanian Dinar", flag: "🇯🇴" },
  { code: "ILS", name: "Israeli Shekel", flag: "🇮🇱" },
  { code: "EGP", name: "Egyptian Pound", flag: "🇪🇬" },
  { code: "MAD", name: "Moroccan Dirham", flag: "🇲🇦" },
  { code: "ZAR", name: "South African Rand", flag: "🇿🇦" },
  { code: "NGN", name: "Nigerian Naira", flag: "🇳🇬" },
  { code: "KES", name: "Kenyan Shilling", flag: "🇰🇪" },
  { code: "BSD", name: "Bahamian Dollar", flag: "🇧🇸" },
  { code: "JMD", name: "Jamaican Dollar", flag: "🇯🇲" },
  { code: "DOP", name: "Dominican Peso", flag: "🇩🇴" },
  { code: "BBD", name: "Barbadian Dollar", flag: "🇧🇧" },
  { code: "TTD", name: "Trinidad & Tobago Dollar", flag: "🇹🇹" },
  { code: "FJD", name: "Fijian Dollar", flag: "🇫🇯" },
];

/**
 * Currency Converter screen — matches mockup #3 (left panel) pixel-close.
 * Light beige background, faint Paris skyline backdrop, big serif numbers,
 * live-rate card, "Good to know" tip card.
 */
export default function CurrencyPanel({ onBack }) {
  const [currencies, setCurrencies] = useState(FALLBACK_CURRENCIES);
  const [amount, setAmount] = useState("100");
  // Persist last-used from/to pair across sessions. Falls back to
  // USD → EUR only for a first-time user with no prior selection.
  // Reading localStorage inside useState's lazy initializer runs
  // exactly once on mount — no flicker, no race with the first fetch.
  const [from, setFrom] = useState(() => {
    try {
      return localStorage.getItem("tiplingo_converter_from_v1") || "USD";
    } catch {
      return "USD";
    }
  });
  const [to, setTo] = useState(() => {
    try {
      return localStorage.getItem("tiplingo_converter_to_v1") || "EUR";
    } catch {
      return "EUR";
    }
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);

  // Whenever either side of the pair changes, save it. Guarded by
  // try/catch because private-browsing Safari can throw on setItem.
  useEffect(() => {
    try {
      localStorage.setItem("tiplingo_converter_from_v1", from);
      localStorage.setItem("tiplingo_converter_to_v1", to);
    } catch {
      /* no-op — quota / private mode */
    }
  }, [from, to]);

  useEffect(() => {
    api
      .get(`/currencies`)
      .then((r) => {
        const list = r.data?.currencies;
        if (Array.isArray(list) && list.length > 0) setCurrencies(list);
      })
      .catch(() => {});
  }, []);

  const [rateUnavailable, setRateUnavailable] = useState(false);

  const convert = useMemo(
    () => async () => {
      const amt = parseFloat(amount);
      if (!Number.isFinite(amt) || amt < 0) {
        setResult(null);
        return;
      }
      setLoading(true);
      try {
        const r = await api.get(`/exchange`, {
          params: { from_currency: from, to_currency: to, amount: amt },
        });
        const rate = Number(r.data?.rate);
        if (Number.isFinite(rate) && rate > 0) {
          try {
            localStorage.setItem(
              RATE_LS_KEY(from, to),
              JSON.stringify({ rate, date: r.data?.date, fetched_at: Date.now() })
            );
          } catch {}
          // Log into the recent conversions activity feed.
          addHistory("currency", {
            amount: amt,
            from,
            to,
            rate,
            result: Number(r.data?.converted) || amt * rate,
          });
        }
        setResult({ ...r.data, stale: false });
        setRateUnavailable(false);
      } catch (e) {
        try {
          const raw = localStorage.getItem(RATE_LS_KEY(from, to));
          const cached = raw ? JSON.parse(raw) : null;
          if (cached?.rate) {
            setResult({
              rate: cached.rate,
              converted: amt * cached.rate,
              date: cached.date,
              stale: true,
            });
            setRateUnavailable(false);
            toast.info("Showing last-known exchange rate (offline)");
          } else {
            // Apple 2.1(a): no red error toast — render an inline soft
            // state in the result card instead so the page never looks
            // broken to an App-Review tester.
            setResult(null);
            setRateUnavailable(true);
          }
        } catch {
          setResult(null);
          setRateUnavailable(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [amount, from, to]
  );

  useEffect(() => {
    const t = setTimeout(convert, 250);
    return () => clearTimeout(t);
  }, [convert]);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const fromInfo = currencies.find((c) => c.code === from);
  const toInfo = currencies.find((c) => c.code === to);
  const today = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="relative min-h-screen pb-6" data-testid="currency-panel">
      <ParisBackdrop />

      {/* Header */}
      <div className="relative z-10 px-5 pt-4 flex items-center justify-between">
        <button
          type="button"
          data-testid="currency-back-btn"
          onClick={onBack}
          aria-label="Back"
          className="w-10 h-10 rounded-full bg-white shadow-soft flex items-center justify-center"
        >
          <CaretLeft size={20} weight="bold" color="#0E2C54" />
        </button>
        <h2 className="font-jakarta font-extrabold text-navy text-xl">Currency Converter</h2>
        <div className="flex items-center gap-1.5">
          {TICKER_ENABLED ? (
            /* Build 85 (D-026) — replaces the redundant header swap
               icon with the 💡 Money Tips trigger. The functional swap
               button lives in the center of the converter card (line
               ~250 below) and is UNCHANGED. */
            <MoneyTipButton
              open={tipsOpen}
              onClick={() => setTipsOpen((v) => !v)}
            />
          ) : (
            <button
              type="button"
              onClick={swap}
              aria-label="Swap"
              className="w-10 h-10 rounded-full bg-white shadow-soft flex items-center justify-center"
            >
              <ArrowsLeftRight size={18} weight="bold" color="#0E2C54" />
            </button>
          )}
          <button
            type="button"
            aria-label="History"
            data-testid="currency-history-btn"
            onClick={() => setHistoryOpen(true)}
            className="w-10 h-10 rounded-full bg-white shadow-soft flex items-center justify-center"
          >
            <Clock size={18} weight="duotone" color="#0E2C54" />
          </button>
        </div>
      </div>

      {/* Money Tips sheet — rendered immediately below the header so
          the panel appears in the user's view the moment they tap the
          💡 button. If we render it below the ticker, iPhone users have
          to scroll past 5+ rate rows to see it. */}
      {TICKER_ENABLED && (
        <div className="relative z-10 px-5 mt-3" data-testid="money-tip-anchor">
          <MoneyTipSheet open={tipsOpen} onOpenChange={setTipsOpen} />
        </div>
      )}

      {/* You send */}
      <div className="relative z-10 px-5 mt-6">
        <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5">
          <div className="text-navy-soft text-sm font-jakarta font-semibold mb-2">You send</div>
          <div className="flex items-center gap-3">
            <input
              data-testid="amount-input"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              className={`flex-1 min-w-0 bg-transparent font-jakarta font-extrabold text-navy outline-none border-b-2 border-navy/15 pb-1 ${fitClass(
                amount,
                CURRENCY_FIT_SIZES,
              )}`}
              placeholder="0"
            />
            <CurrencyDropdown
              value={from}
              onChange={setFrom}
              currencies={currencies}
              info={fromInfo}
              testId="from-select"
            />
          </div>
        </div>

        {/* Swap circle */}
        <div className="flex justify-center -my-3 relative z-20">
          <button
            type="button"
            data-testid="swap-btn"
            onClick={swap}
            aria-label="Swap currencies"
            className="w-12 h-12 rounded-full bg-navy text-white shadow-float flex items-center justify-center hover:bg-navy-hover transition-colors"
          >
            <ArrowsLeftRight size={20} weight="bold" />
          </button>
        </div>

        {/* They receive */}
        <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-5">
          <div className="text-navy-soft text-sm font-jakarta font-semibold mb-2">They receive</div>
          <div className="flex items-center gap-3">
            {(() => {
              const receiveText =
                loading || result?.converted == null
                  ? "—"
                  : Number(result.converted).toFixed(2);
              return (
                <div
                  className={`flex-1 min-w-0 font-jakarta font-extrabold text-navy border-b-2 border-navy/15 pb-1 truncate ${fitClass(
                    receiveText,
                    CURRENCY_FIT_SIZES,
                  )}`}
                  data-testid="receive-amount"
                  title={receiveText}
                >
                  {loading ? (
                    <CircleNotch size={32} className="animate-spin text-navy-soft" />
                  ) : (
                    receiveText
                  )}
                </div>
              );
            })()}
            <CurrencyDropdown
              value={to}
              onChange={setTo}
              currencies={currencies}
              info={toInfo}
              testId="to-select"
            />
          </div>
        </div>

        {/* Live rate card */}
        {result?.rate != null && (
          <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4 mt-5 flex items-center justify-between">
            <div className="flex items-center gap-2 text-navy font-jakarta font-bold text-sm">
              <span>1 {fromInfo?.flag}</span>
              <span className="font-semibold">{from}</span>
              <span className="text-navy-soft">=</span>
              <span className="text-orange font-bold">
                {Number(result.rate).toFixed(5)}
              </span>
              <span>{toInfo?.flag}</span>
              <span className="font-semibold">{to}</span>
            </div>
            <div className="text-right">
              <div className="text-navy-soft text-[10px] font-jakarta font-semibold">{result?.date || today}</div>
              <div className="flex items-center gap-1 text-[10px] font-jakarta font-bold mt-0.5">
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    result.stale ? "bg-orange" : "bg-forest"
                  }`}
                />
                <span className={result.stale ? "text-orange" : "text-forest"}>
                  {result.stale ? "Cached" : "Live rate"}
                </span>
              </div>
              <div className="text-navy-soft text-[10px] font-jakarta font-medium mt-0.5">Updated just now</div>
            </div>
          </div>
        )}

        {/* Inline soft state when live rate is unavailable. Apple 2.1(a)
            compliant — never a red error toast, just a calm affordance
            so the converter never looks broken to a reviewer. */}
        {!result?.rate && rateUnavailable && !loading && (
          <div
            data-testid="currency-rate-unavailable"
            className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4 mt-5 text-center"
          >
            <div className="text-navy-soft text-sm font-jakarta font-medium">
              Live rates updating shortly
            </div>
            <div className="text-navy-soft text-[11px] font-jakarta mt-1">
              We'll refresh automatically — pull down to retry sooner
            </div>
          </div>
        )}

        {/* Good to know — Build 84 permanent bubble. When TICKER_ENABLED
            is ON (Build 85+, D-026), the permanent bubble is replaced by
            the 💡 popover rendered near the header, plus a live ticker
            below the converter card. Both blocks are OFF by default so
            the visible Build 84 UX is preserved bit-for-bit until we
            explicitly enable the flag. */}
        {!TICKER_ENABLED && (
          <div className="bg-orange-soft rounded-2xl border border-orange/10 p-4 mt-4 flex items-start gap-3">
            <div className="text-3xl leading-none">💰</div>
            <div className="flex-1">
              <div className="font-jakarta font-bold text-navy text-sm">Good to know</div>
              <div className="text-navy-soft text-sm font-jakarta font-medium mt-0.5">
                Rates may vary slightly depending on where you exchange.
              </div>
            </div>
          </div>
        )}

        {TICKER_ENABLED && (
          <div className="mt-4 space-y-3" data-testid="currency-ticker-block">
            <CurrencyTicker
              base={from}
              selectedTarget={to}
              onSelectPair={(sym) => {
                // Tapping a ticker row selects that pair. If the tapped
                // symbol matches the current base (edge case: user tries
                // to select the same currency), we swap instead so both
                // sides remain distinct. Otherwise: just update `to`.
                if (!sym) return;
                if (sym === from) {
                  swap();
                } else {
                  setTo(sym);
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Recent conversions sheet */}
      <HistorySheet
        kind="currency"
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onReuse={(entry) => {
          setFrom(entry.from);
          setTo(entry.to);
          setAmount(String(entry.amount));
        }}
      />
    </div>
  );
}

function CurrencyDropdown({ value, onChange, currencies, info, testId }) {
  return (
    <div className="shrink-0 bg-bone rounded-full border border-navy/10 px-3 py-2 flex items-center gap-2 relative">
      <span className="text-xl leading-none">{info?.flag || "🌍"}</span>
      <span className="font-jakarta font-extrabold text-navy text-base">{value}</span>
      <CaretDown size={14} weight="bold" color="#0E2C54" />
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 opacity-0 cursor-pointer"
      >
        {currencies.map((c) => (
          <option key={c.code} value={c.code}>
            {c.flag} {c.code} — {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function ParisBackdrop() {
  return (
    <div
      aria-hidden
      className="absolute inset-0 w-full h-full pointer-events-none bg-no-repeat bg-cover bg-center"
      style={{
        backgroundImage: "url(/assets/backgrounds/currency_world_map.png)",
        opacity: 1,
      }}
    />
  );
}
